import React, { useState, useRef, useEffect } from 'react';
import { MdMap, MdOutlineDiscount } from 'react-icons/md';
import { useRouter } from 'next/router';
import classNames from 'classnames';

import useParams from 'legacy/common/hooks/useParams';
import { useGetMe } from 'legacy/user/hooks';
import { useGetTicketInfoRequired } from 'legacy/events/hooks';
import Container from 'legacy/common/components/layout/Container';
import AlertModal from 'legacy/common/components/modal/AlertModal';
import Title from 'legacy/events/components/event-page/Title';
import About from 'legacy/events/components/event-page/About';
import FadeIn from 'legacy/common/components/transition/FadeIn';
import ScanQRModal from 'legacy/events/components/scanQR/ScanQRModal';
import CoverImage from 'legacy/events/components/event-page/CoverImage';
import EventSkeleton from 'legacy/events/components/event-page/EventSkeleton';
import ContactHostModal from 'legacy/events/components/contact-host/ContactHostModal';
import TicketModalNew from 'legacy/tickets/components/TicketModalNew';
import Background from 'legacy/common/components/wysiwyg-form/Background';
import EventDate from '../components/event/EventDate';
import Divider from 'legacy/common/components/wysiwyg-form/Divider';
import OrganizersBadges from 'legacy/common/components/wysiwyg-form/OrganizersBadges';
import OrganizersGuestsModal from 'legacy/common/components/wysiwyg-form/OrganizersGuestsModal';
import GuestlistBadges from '../components/event/GuestlistBadges';
import EventActionBar from '../components/event-page/EventActionBar';
import EventQuestions from '../components/event/EventQuestions';
import Header from 'legacy/common/components/wysiwyg-form/Header';
import ShareEvent from '../components/event-page/ShareEvent';
import { CodeType, EventForHost } from '../types';
import EventMenu from '../components/event-page/EventMenu';
import RecurringEventBadge from '../components/event/RecurringEventBadge';
import GuestGridList from '../components/guest-list/GuestGridList';
import MomentumChip from 'components/MomentumChip';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FLAGS } from 'legacy/common/constants/launchdarkly';
import { useIsDesktop } from 'hooks/useResponsiveQueries';
import { useViewEvent } from 'features/events/context/ViewEvent';
import { useTrackEventImpressionMutation } from 'api/queries/events/event-impressions';
import { EventPageProvider } from 'legacy/events/components/event-page/context/EventPageContext';
import { getEventPricing } from 'legacy/events/helpers';
import { Trans, useTranslation } from 'react-i18next';
import { currencyFormat } from 'legacy/common/utils/currency';
import { useIntercom } from 'features/intercom/context/IntercomProvider';
import { isBounceUOttawa } from 'helpers/environments';

const EventPage = () => {
  const { t } = useTranslation(['events']);
  const flags = useFlags();
  const router = useRouter();
  const params = useParams<{
    slug: string;
    tierShareToken: string;
    invite: string;
    discountCode: string;
    p: string;
  }>();
  const id = params.slug;
  const isDesktop = useIsDesktop();
  const {
    unlockedTierError,
    onUnlockTierErrorReset,
    discountCodeData,
    isCodeVerified,
    discountCodeError,
    event,
    isEventLoading,
    eventError
  } = useViewEvent();
  const { data: user } = useGetMe();
  const { data: ticketInfoRequired, refetch: refetchTicketInfoRequired } =
    useGetTicketInfoRequired(id);
  const { mutate: createEventImpression } = useTrackEventImpressionMutation();
  const hasCreatedImpression = useRef(false);

  const { setHideIntercom } = useIntercom();

  useEffect(() => {
    if (event?.isOrganizer || event?.isScanner || event?.isPhotographer) {
      setHideIntercom(true);
    }

    return () => {
      setHideIntercom(false);
    };
  }, [setHideIntercom, event]);

  if (event && !hasCreatedImpression.current) {
    createEventImpression(event._id);
    hasCreatedImpression.current = true;
  }

  const [isTicketModalOpen, setIsTicketModalOpen] = useState(false);
  const [isContactHostModalOpen, setIsContactHostModalOpen] = useState(false);
  const [isOrganizersModalOpen, setIsOrganizersModalOpen] = useState(false);
  const [showGuestsModal, setShowGuestsModal] = useState(false);
  const [showScanQRModal, setShowScanQRModal] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isManageEventModalOpen, setIsManageEventModalOpen] = useState(false);

  const toggleCheckout = () => {
    router.push(`/events/${id}/checkout${location.search}`);
  };

  const toggleOrganizersModal = () => {
    setIsOrganizersModalOpen((prevState) => !prevState);
  };

  const viewGuests = () => {
    setShowGuestsModal(true);
  };

  const toggleScanQRModal = () => {
    setShowScanQRModal((showScanQRModal) => !showScanQRModal);
  };

  const toggleShareModal = () => {
    setIsShareModalOpen((shareModalOpen) => !shareModalOpen);
  };

  const toggleManageEvent = () => {
    setIsManageEventModalOpen((manageEventOpen) => !manageEventOpen);
  };

  const toggleViewContactsModal = () => {
    setIsContactHostModalOpen((contactModalOpen) => !contactModalOpen);
  };

  const organizers = {
    groups: event?.groups,
    operators: event?.operators,
    scanners: event?.scanners,
    photographers: event?.photographers,
    cohosts: event?.cohosts,
    host: event?.host
  };

  if (isEventLoading) {
    return <EventSkeleton />;
  }

  if (eventError?.message) {
    return (
      <Container>
        <div className="my-20 text-center text-2xl text-gray-700">
          {eventError.message}
        </div>
      </Container>
    );
  }

  if (!event) {
    return null;
  }

  return (
    <>
      <Header
        isDraft={event.isDraft}
        title={t('events:eventDetails.title')}
        onBack={() => {
          router.back();
        }}
        onMenuClick={!event.isScanner && toggleManageEvent}
      />
      <FadeIn>
        <div className="-mt-[10px] md:-mt-0 md:pt-[27px] lg:pt-[57px] pb-20">
          {params.discountCode &&
            discountCodeData?.codeType === CodeType.DiscountCode &&
            isCodeVerified && (
              <div className="z-20 relative h-[50px] flex justify-center align-center bg-green-100 text-green-900 font-normal text-base">
                <MdOutlineDiscount className="mr-1" />
                <Trans
                  i18nKey="events:eventDetails.discountApplied"
                  values={{
                    code: discountCodeData.code,
                    value:
                      discountCodeData.discount.kind === 'amount'
                        ? currencyFormat(discountCodeData.discount.value)
                        : `${discountCodeData.discount.value}%`
                  }}
                  components={{
                    strong: <strong style={{ marginRight: '4px' }} />
                  }}
                />
              </div>
            )}
          {params.p &&
            discountCodeData?.code === params.p &&
            isCodeVerified && (
              <div className="z-20 -mt-[41px] md:-mt-[11px] relative h-[50px] flex justify-center align-center bg-green-100 text-green-900 font-normal text-base gap-1">
                <img
                  src="/img/icons/award-star.svg"
                  alt="Events link"
                  height={18}
                  width={18}
                  className="inline"
                />
                {discountCodeData?.discount ? (
                  <>
                    <Trans
                      i18nKey="events:eventDetails.discountApplied"
                      values={{
                        code: discountCodeData.code,
                        value:
                          discountCodeData.discount.kind === 'amount'
                            ? currencyFormat(discountCodeData.discount.value)
                            : `${discountCodeData.discount.value}%`
                      }}
                      components={{
                        strong: <strong style={{ marginRight: '4px' }} />
                      }}
                    />
                  </>
                ) : (
                  <>
                    <Trans
                      i18nKey="events:eventDetails.discountAppliedLockIn"
                      values={{
                        code: discountCodeData.code
                      }}
                      components={{
                        strong: <strong style={{ marginRight: '4px' }} />
                      }}
                    />
                  </>
                )}
              </div>
            )}
          {discountCodeError && (
            <div className="z-20 -mt-[41px] md:-mt-[11px] relative h-[50px] flex justify-center align-center bg-red-100 text-red-900 font-normal text-base gap-1">
              <MdOutlineDiscount className="mr-1" />
              {discountCodeError.message}
            </div>
          )}
          <div
            className={classNames(
              '-mt-[90px] md:-mt-[140px] pb-20 md:w-[630px] md:mx-auto',
              event.seriesId
                ? 'pt-[80px] md:pt-[160px]'
                : 'pt-[90px] md:pt-[180px]'
            )}
          >
            <Background photoUrl={event?.photo?.url} />
            <div className="relative px-4 z-20">
              <div className="flex mx-4 pb-4">
                <RecurringEventBadge
                  eventId={event._id}
                  seriesId={event.seriesId}
                />
              </div>
              <CoverImage photoUrl={event.photo.url} />
              <Title title={event.title} isPrivate={event.private} />
              {flags[FLAGS.ENABLE_EVENT_MOMENTUM_CHIP] &&
                event?.momentumChip && (
                  <div
                    className={`${
                      isDesktop
                        ? 'flex justify-center w-[360px] mt-[16px] mb-[2px]'
                        : 'flex justify-start w-full mt-[16px] mb-[2px]'
                    }`}
                  >
                    <MomentumChip
                      type={event.momentumChip.type}
                      displayCount={event.momentumChip.displayCount}
                    />
                  </div>
                )}
              <div className="pt-4 text-lg font-semibold text-[#00000DDE]">
                {event.address}
              </div>
              <EventDate
                activeStatus={event.activeStatus}
                startDate={event.startDate}
                endDate={event.endDate}
              />
              {event?.coordinates?.length > 0 && (
                <a
                  className="text-lg font-semibold flex align-center gap-2 mt-4 text-primary-400"
                  target="_blank"
                  href={
                    isBounceUOttawa()
                      ? `https://maps.apple.com/?q=${event.coordinates[1]},${event.coordinates[0]}`
                      : `https://www.google.com/maps/?q=${event.coordinates[1]},${event.coordinates[0]}`
                  }
                  rel="noreferrer"
                >
                  <MdMap size={20} />
                  {t('events:eventDetails.seeLocation')}
                </a>
              )}

              <EventQuestions
                requiredInfo={ticketInfoRequired}
                event={event}
                refetchTicketInfoRequired={refetchTicketInfoRequired}
              />

              <OrganizersBadges
                onClick={toggleOrganizersModal}
                isEditable={false}
                data={organizers}
              />
              {event.guestPreview &&
                (event.isOrganizer || event.isScanner || event.showGuests) && (
                  <GuestlistBadges
                    guestsPreview={event.guestPreview}
                    totalGuests={event.totalGuests}
                    onClick={viewGuests}
                  />
                )}

              <Divider />

              <About description={event.description} />

              {/* Modals */}
              <OrganizersGuestsModal
                data={organizers}
                isOpen={isOrganizersModalOpen}
                onClose={toggleOrganizersModal}
              />
              {showGuestsModal && (
                <GuestGridList
                  eventId={event?._id}
                  onClose={() => {
                    setShowGuestsModal(false);
                  }}
                />
              )}
            </div>
          </div>
        </div>

        <EventPageProvider
          value={{
            event,
            user,
            toggleManageEvent,
            toggleScanQRModal,
            toggleShareModal,
            setIsTicketModalOpen,
            toggleViewContactsModal,
            toggleCheckout,
            viewGuests,
            isManageEventModalOpen,
            canShare: !(event.private && !event.isOrganizer),
            priceRangeNoDiscount: getEventPricing(event),
            isLoggedIn: Boolean(user)
          }}
        >
          <EventActionBar />
          <EventMenu />
        </EventPageProvider>
      </FadeIn>

      {unlockedTierError?.message && (
        <AlertModal
          onSubmit={onUnlockTierErrorReset}
          message={unlockedTierError.message}
        />
      )}

      {isTicketModalOpen && (
        <TicketModalNew
          eventId={event?._id}
          onClose={() => setIsTicketModalOpen(false)}
        />
      )}

      {isContactHostModalOpen && user && (
        <ContactHostModal
          eventId={event._id}
          onClose={() => {
            setIsContactHostModalOpen(false);
          }}
        />
      )}

      {showScanQRModal && (
        <ScanQRModal
          eventId={event._id}
          onClose={() => {
            setShowScanQRModal(false);
          }}
        />
      )}

      <ShareEvent
        eventId={event._id}
        isPrivate={event.private}
        isOrganizer={event.isOrganizer}
        shareToken={(event as EventForHost).shareToken}
        visible={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
      />
    </>
  );
};

export default EventPage;
